<sz-app-shell>
  <sz-top-bar />

  @if (completedDrill(); as cd) {
    <div class="drill-completed">
      @if (hasPreviousDrill()) {
        <button type="button" class="drill-completed__prev" (click)="goToPreviousDrill()">
          <sz-icon name="arrow-back" [size]="12" />
          <span class="drill-completed__prev-label">
            {{ 'session.active.previousDrillCta' | translate }}
            @if (previousDrillName(); as prev) {
              <span class="drill-completed__prev-name">· {{ prev }}</span>
            }
          </span>
        </button>
      }

      <sz-page-title
        [title]="'session.drillCompleted.titleMain' | translate"
        [accent]="'session.drillCompleted.titleAccent' | translate"
        size="xl">
        @if (planTitle(); as title) {
          <div szTitleKicker class="drill-completed__kicker">
            <span>{{ 'session.drillCompleted.planLabel' | translate }} · {{ title }}</span>
          </div>
        }
        <div szTitleSub class="drill-completed__sub">
          <sz-num-big
            [value]="completedPosition()"
            [unit]="'session.drillCompleted.ofUnit' | translate: { total: totalDrills() }" />
          <span class="drill-completed__delta">
            {{ 'session.drillCompleted.progressLabel' | translate: { percent: progressPercent() } }}
          </span>
        </div>
      </sz-page-title>

      <sz-card variant="featured">
        <div class="drill-completed__card-head">
          <span class="drill-completed__check-label">
            <sz-icon name="check" [size]="13" />
            {{ 'session.drillCompleted.completedTag' | translate }}
          </span>
          <sz-pill variant="outline">{{ scoringLabelOf(cd.scoring) }}</sz-pill>
        </div>
        <div class="drill-completed__drill-name">{{ cd.drillName }}</div>
        <div class="drill-completed__tiles">
          @for (tile of completedTiles(); track tile.label) {
            <sz-tile [label]="tile.label" [value]="tile.value" [unit]="tile.unit" [variant]="tile.variant" />
          }
        </div>
      </sz-card>

      @if (nextDrill(); as nd) {
        <sz-section-head [label]="'session.drillCompleted.upNextLabel' | translate" />
        <sz-row-card variant="flat">
          <sz-ic-circle szRowLead size="sm" accent="ink">
            <sz-icon name="clock" [size]="12" />
          </sz-ic-circle>
          <div class="drill-completed__next-body">
            <div class="drill-completed__next-name">{{ nd.drillName }}</div>
            <div class="drill-completed__next-meta">
              {{ scoringLabelOf(nd.scoring) }}{{ nd.targetRuns ? ' · ' + nd.targetRuns + ' runs' : '' }}
            </div>
          </div>
        </sz-row-card>
      }

      <sz-section-head [label]="'session.drillCompleted.planProgressLabel' | translate" />
      <div class="drill-completed__timeline">
        @for (entry of timeline(); track entry.id) {
          @if (entry.status === 'done') {
            <div class="drill-completed__entry-group" [class.drill-completed__entry-group--open]="isExpanded(entry.id)">
              <sz-row-card
                variant="flat"
                class="drill-completed__entry-head"
                role="button"
                tabindex="0"
                [interactive]="true"
                [attr.aria-expanded]="isExpanded(entry.id)"
                [attr.aria-label]="'session.summary.expandRunsAria' | translate: { drill: entry.drillName }"
                (click)="toggleEntry(entry.id)"
                (keydown.enter)="toggleEntry(entry.id)"
                (keydown.space)="toggleEntry(entry.id)">
                <sz-ic-circle szRowLead size="sm" accent="lime">
                  <sz-icon name="check" [size]="12" />
                </sz-ic-circle>
                <div class="drill-completed__entry-body">
                  <div class="drill-completed__entry-name">{{ entry.drillName }}</div>
                  <div class="drill-completed__entry-meta">
                    {{ 'session.summary.runsCount' | translate: { count: entry.runsCount } }}
                  </div>
                </div>
                <div szRowEnd class="drill-completed__entry-end">
                  <div class="drill-completed__entry-best">
                    <div class="drill-completed__entry-best-value">{{ entry.bestValue }}</div>
                    @if (entry.showBestLabel) {
                      <div class="drill-completed__entry-best-label">{{ 'session.summary.bestLabel' | translate }}</div>
                    }
                  </div>
                  <span class="drill-completed__chevron">
                    <sz-icon name="chevron-right" [size]="14" />
                  </span>
                </div>
              </sz-row-card>

              @if (isExpanded(entry.id)) {
                <div class="drill-completed__runs">
                  @if (entry.runRows.length > 0) {
                    <lib-session-rep-list [rows]="entry.runRows" [interactive]="false" />
                  } @else {
                    <p class="drill-completed__runs-empty">{{ 'session.summary.noRuns' | translate }}</p>
                  }
                </div>
              }
            </div>
          } @else {
            <sz-row-card variant="flat" [class.drill-completed__entry--queue]="entry.status === 'queue'">
              @if (entry.status === 'next') {
                <sz-ic-circle szRowLead size="sm" accent="ink">
                  <sz-icon name="clock" [size]="12" />
                </sz-ic-circle>
              } @else {
                <sz-ic-circle szRowLead size="sm" accent="ghost">
                  {{ entry.index.toString().padStart(2, '0') }}
                </sz-ic-circle>
              }
              <div class="drill-completed__entry-body">
                <div class="drill-completed__entry-name">{{ entry.drillName }}</div>
                <div class="drill-completed__entry-meta">{{ entry.scoringLabel }}</div>
              </div>
              @if (entry.status === 'next' || entry.removable) {
                <div szRowEnd class="drill-completed__entry-end">
                  @if (entry.status === 'next') {
                    <sz-pill variant="outline">{{ 'session.drillCompleted.nextPill' | translate }}</sz-pill>
                  }
                  @if (entry.removable) {
                    <sz-icon-button
                      icon="x"
                      size="sm"
                      [ariaLabel]="'session.drillCompleted.removeDrillAria' | translate: { drill: entry.drillName }"
                      (click)="confirmRemoveEntry(entry)" />
                  }
                </div>
              }
            </sz-row-card>
          }
        }

        @if (canAddDrill()) {
          <div class="drill-completed__add">
            <div class="drill-completed__add-rule" aria-hidden="true"></div>
            <button type="button" class="drill-completed__add-btn" (click)="goToAddDrill()">
              <sz-icon name="plus" [size]="12" />
              {{ 'session.drillCompleted.addDrillCta' | translate }}
            </button>
          </div>
        }
      </div>
    </div>

    <sz-cta-bar variant="floating">
      @if (nextDrill(); as nd) {
        <sz-button variant="primary" [fullWidth]="true" (click)="continueSession()">
          {{ 'session.drillCompleted.continueWithNext' | translate: { next: nd.drillName } }}
          <sz-icon name="arrow-right" [size]="16" />
        </sz-button>
      } @else {
        <sz-button variant="primary" [fullWidth]="true" (click)="finishSession()">
          {{ 'session.drillCompleted.finishSession' | translate }}
          <sz-icon name="arrow-right" [size]="16" />
        </sz-button>
      }
    </sz-cta-bar>
  } @else {
    <p class="drill-completed__loading">{{ 'common.loading' | translate }}</p>
  }
</sz-app-shell>
